/**
 * Permission Matcher Utility
 *
 * Provides wildcard-aware permission matching for ABAC permissions.
 * Permission format: module:resource:action:scope[:field]
 *
 * Examples:
 *   - 'crm:lead:read:tenant' - Read all leads in tenant
 *   - 'crm:*' - Full CRM access (wildcard)
 *   - 'hrms:employee:*:self' - All actions on own employee record
 */

export type Scope = 'self' | 'team' | 'department' | 'tenant' | 'global';

export interface ParsedPermission {
  module: string;
  resource: string;
  action: string;
  scope: Scope;
  field?: string;
  raw: string;
}

/**
 * Parse a permission string into its components.
 */
export function parsePermission(permission: string): ParsedPermission | null {
  const parts = permission.split(':');

  if (parts.length < 4) {
    return null;
  }

  const [module, resource, action, scope, field] = parts;

  if (!scope || !isValidScope(scope)) {
    return null;
  }

  return {
    module: module ?? '',
    resource: resource ?? '',
    action: action ?? '',
    scope,
    field,
    raw: permission,
  };
}

/**
 * Check if a scope string is valid.
 */
export function isValidScope(value: string): value is Scope {
  return ['self', 'team', 'department', 'tenant', 'global'].includes(value);
}

export function normalizePermissionClaims(userPermissions: readonly string[] = []): string[] {
  return userPermissions
    .map((permission) => permission.trim())
    .filter((permission) => permission.length > 0 && permission !== '*');
}

/**
 * Scope hierarchy for comparison.
 * Higher value = broader scope.
 */
const SCOPE_HIERARCHY: Record<Scope, number> = {
  self: 10,
  team: 20,
  department: 30,
  tenant: 40,
  global: 50,
};

/**
 * Check if user's scope covers the required scope.
 */
export function scopeCovers(userScope: Scope, requiredScope: Scope): boolean {
  return SCOPE_HIERARCHY[userScope] >= SCOPE_HIERARCHY[requiredScope];
}

/**
 * Match a single permission against a required permission.
 * Supports wildcards: '*', 'crm:*', 'crm:lead:*', etc.
 */
export function matchSinglePermission(userPerm: string, required: string): boolean {
  const normalizedUserPermission = userPerm.trim();
  if (!normalizedUserPermission || normalizedUserPermission === '*') return false;

  // Direct match
  if (normalizedUserPermission === required) return true;

  // Wildcard ending with :*
  if (normalizedUserPermission.endsWith(':*')) {
    const prefix = normalizedUserPermission.slice(0, -2);
    // Check if required starts with the prefix
    if (required === prefix) return true;
    if (required.startsWith(prefix + ':')) return true;
  }

  // Handle wildcards in the middle (e.g., 'crm:*:read:tenant')
  const userParts = normalizedUserPermission.split(':');
  const reqParts = required.split(':');

  if (userParts.length !== reqParts.length && !normalizedUserPermission.endsWith(':*')) {
    return false;
  }

  for (let i = 0; i < userParts.length; i++) {
    const userPart = userParts[i];
    const reqPart = reqParts[i];

    if (userPart === '*') continue;
    if (userPart !== reqPart) return false;
  }

  return true;
}

/**
 * Match user permissions against a required permission.
 * Returns true if ANY user permission grants access.
 */
export function matchPermission(userPermissions: string[], required: string): boolean {
  if (!userPermissions?.length || !required) return false;
  const normalizedPermissions = normalizePermissionClaims(userPermissions);

  // Check each user permission
  for (const perm of normalizedPermissions) {
    if (matchSinglePermission(perm, required)) {
      return true;
    }
  }

  // Check if user has broader wildcard permissions
  // e.g., user has 'crm:*', checking 'crm:lead:read:tenant'
  const parts = required.split(':');
  for (let i = parts.length; i > 0; i--) {
    const wildcard = parts.slice(0, i).join(':') + ':*';
    if (normalizedPermissions.includes(wildcard)) {
      return true;
    }
    // Also check without :* for module-level wildcards
    const moduleWildcard = parts.slice(0, i).join(':');
    if (i === 1 && normalizedPermissions.includes(moduleWildcard + ':*')) {
      return true;
    }
  }

  return false;
}

/**
 * Check if user has access to a module.
 */
export function hasModuleAccess(userPermissions: string[], module: string): boolean {
  if (!userPermissions?.length) return false;
  const normalizedPermissions = normalizePermissionClaims(userPermissions);

  // Module wildcard
  if (normalizedPermissions.includes(`${module}:*`)) return true;

  // Any permission in the module
  return normalizedPermissions.some((p) => p.startsWith(`${module}:`));
}

/**
 * Get all modules the user has access to.
 */
export function getAccessibleModules(userPermissions: string[]): string[] {
  if (!userPermissions?.length) return [];
  const normalizedPermissions = normalizePermissionClaims(userPermissions);

  const modules = new Set<string>();

  for (const perm of normalizedPermissions) {
    const parts = perm.split(':');
    if (parts[0] && parts[0] !== '*') {
      modules.add(parts[0]);
    }
  }

  return Array.from(modules);
}

/**
 * Create a permission string.
 */
export function createPermission(
  module: string,
  resource: string,
  action: string,
  scope: Scope,
  field?: string,
): string {
  const parts = [module, resource, action, scope];
  if (field) parts.push(field);
  return parts.join(':');
}
